import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const isOmniPage = location.pathname === '/omniagent';
  const isOfferings = location.pathname === '/offerings';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solutions = [
    { label: 'Voxit OmniAgent (AI)', href: '/omniagent' },
    { label: 'TotalFarm.Pro', href: '/totalfarm' },
    { label: 'TechIQ Smart Farming Box', href: '/techiqbox' },
    { label: 'FyntQuick Loan', href: '/fyntloan' },
    { label: 'SOMO Learn', href: '/somolearn' },
    { label: 'SwiftGuard Responder', href: '/swiftguard' },
  ];

  const services = [
    { label: 'App Development', href: '/services/app-development' },
    { label: 'Web Development', href: '/services/web-development' },
    { label: 'BPO Services', href: '/services/bpo' },
    { label: 'AI Solutions', href: '/services/ai-solutions' },
    { label: 'Cloud Services', href: '/services/cloud' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : isOmniPage ? 'bg-transparent' : 'bg-[#0a0a0f]/80 backdrop-blur-md'
      }`}
      style={{ padding: '0 24px' }}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#fe5959]">
              <span className="text-white text-xl font-bold">V</span>
            </div>
            <div className="flex flex-col">
              <span className={`text-lg font-bold leading-tight ${isScrolled ? 'text-[#050707]' : isOmniPage ? 'text-[#232323]' : 'text-white'}`}>VoxIT</span>
              <span className={`text-xs leading-tight ${isScrolled ? 'text-gray-500' : isOmniPage ? 'text-gray-600' : 'text-gray-400'}`}>Media Consulting</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-600 hover:text-[#050707]' : isOmniPage ? 'text-[#353535] hover:text-[#232323]' : 'text-gray-300 hover:text-white'}`}
            >
              Home
            </Link>
            
            {/* Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsSolutionsOpen(true)}
              onMouseLeave={() => setIsSolutionsOpen(false)}
            >
              <button
                className={`text-sm font-medium transition-colors flex items-center gap-1 py-2 ${isScrolled ? 'text-gray-600 hover:text-[#050707]' : isOmniPage ? 'text-[#353535] hover:text-[#232323]' : 'text-gray-300 hover:text-white'}`}
              >
                Solutions <ChevronDown size={14} />
              </button>
              
              {isSolutionsOpen && (
                <div className="absolute top-full left-0 pt-1 w-64">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                    {solutions.map((solution) => (
                      <Link
                        key={solution.label}
                        to={solution.href}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#fe5959] transition-colors border-b border-gray-50 last:border-0"
                        onClick={() => setIsSolutionsOpen(false)}
                      >
                        {solution.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`text-sm font-medium transition-colors flex items-center gap-1 py-2 ${isScrolled ? 'text-gray-600 hover:text-[#050707]' : isOmniPage ? 'text-[#353535] hover:text-[#232323]' : 'text-gray-300 hover:text-white'}`}
              >
                Services <ChevronDown size={14} />
              </button>
              
              {isServicesOpen && (
                <div className="absolute top-full left-0 pt-1 w-56">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                    {services.map((service) => (
                      <Link
                        key={service.label}
                        to={service.href}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#fe5959] transition-colors border-b border-gray-50 last:border-0"
                        onClick={() => setIsServicesOpen(false)}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/offerings"
              className={`text-sm font-medium transition-colors ${isOfferings ? 'text-[#fe5959]' : isScrolled ? 'text-gray-600 hover:text-[#050707]' : isOmniPage ? 'text-[#353535] hover:text-[#232323]' : 'text-gray-300 hover:text-white'}`}
            >
              Offerings
            </Link>

            <a
              href="#contact"
              className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-600 hover:text-[#050707]' : isOmniPage ? 'text-[#353535] hover:text-[#232323]' : 'text-gray-300 hover:text-white'}`}
            >
              Contact
            </a>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a href="#contact" className="bg-[#fe5959] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#d65151] transition-colors">
              Get Started
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X size={24} className={isScrolled ? 'text-gray-800' : isOmniPage ? 'text-[#232323]' : 'text-white'} />
            ) : (
              <Menu size={24} className={isScrolled ? 'text-gray-800' : isOmniPage ? 'text-[#232323]' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden py-4 border-t border-gray-200 bg-white rounded-b-xl"
          >
            <nav className="flex flex-col gap-2">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-[#fe5959] transition-colors py-3 px-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <div className="px-4 py-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Solutions</span>
              </div>
              {solutions.map((solution) => (
                <Link
                  key={solution.label}
                  to={solution.href}
                  className="text-sm font-medium text-gray-600 hover:text-[#fe5959] transition-colors py-2 px-6"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {solution.label}
                </Link>
              ))}
              <Link
                to="/offerings"
                className={`text-sm font-semibold transition-colors py-3 px-4 ${isOfferings ? 'text-[#fe5959]' : 'text-gray-700 hover:text-[#fe5959]'}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Offerings
              </Link>
              <a
                href="#contact"
                className="bg-[#fe5959] text-white text-center py-3 rounded-full text-sm font-semibold mx-4 mt-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Started
              </a>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
